import { FC } from 'react'
import styles from './styles.module.scss'
import { Colour, SectionPaddingDefault } from '../constants'
import { Panel, PanelItem, PanelLayout } from '../Panel'
import cx from 'classnames'
import { Link } from '../Link'
import { useContributorUrl } from '../Blog/hooks/useContributorUrl'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import { CmsPageSectionBase } from '../ComposedPage/types'
import { CmsAuthor } from '../CardPanel/CardPanelTypes'
import { AuthorImage, AuthorImageSize } from '../AuthorImage/AuthorImage'
import { RichTextContent, useRichText } from '@/hooks/useRichText'
import { Options } from '@contentful/rich-text-react-renderer'

export interface CmsQuoteCard extends CmsPageSectionBase {
  fields: CmsPageSectionBase['fields'] & {
    id?: string
    title?: string
    attribution?: string
    ctaText?: string
    ctaUrl?: string
    richText?: RichTextContent
    backgroundColour: Colour
    author?: CmsAuthor
    cardPanel?: boolean
  }
}

export const QuoteCard: FC<CmsQuoteCard> = ({
  fields,
  blog,
  isPageSection,
}) => {
  const {
    backgroundColour,
    author,
    richText,
    sectionPaddingTop,
    sectionPaddingBottom,
    cardPanel,
  } = fields
  const url = useContributorUrl(author?.fields?.slug ?? '')
  const options: Options = {
    renderNode: {
      [BLOCKS.HEADING_4]: (_, children) => {
        return (
          <h4 className={styles.heading4}>
            <span className={styles.openingQuote}>&ldquo;</span>
            {children}&rdquo;
          </h4>
        )
      },
      [INLINES.HYPERLINK]: (node, children) => {
        const uri = (node.data && (node.data as { uri?: string }).uri) || ''
        return <Link url={uri}>{children}</Link>
      },
    },
  }
  const Content = () => {
    return (
      <div
        className={cx(styles.quoteCard, styles[backgroundColour], {
          [styles.inCardPanel]: Boolean(cardPanel),
          [styles.isBlog]: blog,
          [styles.noPad]: blog && backgroundColour === Colour.warmStone,
        })}
      >
        {!blog && author?.fields.photo && (
          <AuthorImage
            className={styles.authorImage}
            photo={author?.fields?.photo}
            size={AuthorImageSize.MEDIUM}
            name={author?.fields?.name}
          />
        )}
        <div className={styles.contentSection}>
          {useRichText(richText, options)}
          <div className={styles.authorDetails}>
            {blog && author?.fields.photo && (
              <AuthorImage
                className={styles.authorImage}
                photo={author?.fields?.photo}
                name={author?.fields?.name}
              />
            )}
            <div>
              <Link backgroundColour={backgroundColour} url={url}>
                {author?.fields?.name}
              </Link>
              <div className={styles.authorPosition}>
                {`${author?.fields?.jobTitle ?? ''}${author?.fields?.company ? `, ${author.fields.company}` : ''}`}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return isPageSection ? (
    <Panel
      className={cx(
        styles.quoteSection,
        styles[sectionPaddingTop || SectionPaddingDefault.TOP],
        styles[sectionPaddingBottom || SectionPaddingDefault.BOTTOM],
      )}
      layout={PanelLayout.full}
      short
      colour={Colour[backgroundColour]}
    >
      <PanelItem>
        <Content />
      </PanelItem>
    </Panel>
  ) : (
    <Content />
  )
}

QuoteCard.displayName = 'Quote Card'
